import { type ComponentType, forwardRef } from 'react';
import BalanceOutlined from '@mui/icons-material/BalanceOutlined';
import ChildCareOutlined from '@mui/icons-material/ChildCareOutlined';
import EmojiNatureOutlined from '@mui/icons-material/EmojiNatureOutlined';
import FactoryOutlined from '@mui/icons-material/FactoryOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import HealthAndSafetyOutlined from '@mui/icons-material/HealthAndSafetyOutlined';
import LandscapeOutlined from '@mui/icons-material/LandscapeOutlined';
import LockClockOutlined from '@mui/icons-material/LockClockOutlined';
import PublicOffOutlined from '@mui/icons-material/PublicOffOutlined';
import PublicOutlined from '@mui/icons-material/PublicOutlined';
import WaterDropOutlined from '@mui/icons-material/WaterDropOutlined';
import { type OverridableComponent } from '@mui/material/OverridableComponent';
import type { SvgIconProps, SvgIconTypeMap } from '@mui/material/SvgIcon';
import { EnvironmentalRisks, GovernanceRisks, HumanRightsRisks, RiskLevels, type RiskLevelsEnum, type RisksEnum } from '../types/enums';

export const SupplierRisksIcons: Record<RisksEnum, OverridableComponent<SvgIconTypeMap>> = {
   [HumanRightsRisks.CHILD_LABOR]: ChildCareOutlined,
   [HumanRightsRisks.FORCED_LABOR]: LockClockOutlined,
   [HumanRightsRisks.WORKING_CONDITIONS]: HealthAndSafetyOutlined,
   [HumanRightsRisks.COLLECTIVE_BARGAINING]: GroupsOutlined,
   [HumanRightsRisks.DISCRIMINATION]: BalanceOutlined,
   [HumanRightsRisks.LAND_RIGHTS]: LandscapeOutlined,
   [HumanRightsRisks.FUNDAMENTAL_RIGHTS]: GavelOutlined,
   [EnvironmentalRisks.POLLUTION]: FactoryOutlined,
   [EnvironmentalRisks.DEFORESTATION_BIODIVERSITY]: EmojiNatureOutlined,
   [EnvironmentalRisks.WASTE]: PublicOffOutlined,
   [EnvironmentalRisks.WATER_CONSUMPTION]: WaterDropOutlined,
   [GovernanceRisks.CORRUPTION]: HandshakeOutlined,
   [GovernanceRisks.POLITICAL_STABILITY]: PublicOutlined,
};

const RISK_LEVEL_COLORS: Record<RiskLevelsEnum, string> = {
   [RiskLevels.LOW]: 'success.main',
   [RiskLevels.MEDIUM]: 'warning.main',
   [RiskLevels.HIGH]: 'error.main',
};

type SupplierRiskIconProps = {
   risk: keyof typeof SupplierRisksIcons;
   level?: RiskLevelsEnum | null;
   fontSize?: SvgIconProps['fontSize'];
};

const getRiskColor = (level?: RiskLevelsEnum | null): string => {
   if (!level) {
      return 'inherit';
   }
   return RISK_LEVEL_COLORS[level] ?? 'inherit';
};

const SupplierRiskIcon = forwardRef<SVGSVGElement, SupplierRiskIconProps>(({ risk, level, fontSize = 'small', ...props }, ref) => {
   const icon = SupplierRisksIcons[risk];

   if (!icon) {
      return null;
   }

   const color = getRiskColor(level);

   const IconComponent = icon as ComponentType<SvgIconProps>;

   return <IconComponent ref={ref} fontSize={fontSize} sx={{ color }} {...props} />;
});

SupplierRiskIcon.displayName = 'SupplierRiskIcon';

export default SupplierRiskIcon;
